import { addEmployeeBeneficiaryV2 } from '@api/apiCall';
import { fetchProfile } from '@api/useQueryCalls';
import InfoTextComponent from '@atoms/InfoTextComponent';
import { View } from '@components/primitives';
import useFeatureFlag from '@customHooks/useFeatureFlag';
import useFetchBanks from '@customHooks/useFetchBanks';
import { useToast } from '@hooks';
import ScreenNames from '@navigation/navigationConstants';
import { shouldShowError } from '@organisms/HomeTab/homeUtils';
import { useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { BOTTOMSHEET_CONTENT, BOTTONSHEET_KEYS, FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { bankAddSchema } from '@utils/schemas';
import { formatUnderScoreLowerCase, returnBottomSheetContent } from '@utils/utils';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addBankHandlers, renderFormContentFunction, shouldRenderSuccessfulBankAdd } from './addBankUtils';
import { logBankAccountAddPress, logCloseHandler } from './analyticsBankScreen';
import styles from './styles';

// Define some of the types you will need
interface Bank {
  accountNumber: string;
  id?: string | number;
  [key: string]: any;
}

interface Values {
  accountNumber: string;
  accountName: string;
  accountNickName: string;
  selectedBank: { bankName: string };
}

type ModalData = {
  visible: boolean;
  data?: any;
  errorCode?: string;
};

interface InfoModal {
  visible: boolean;
  data?: unknown;
  errorCode?: string;
}

type Source = string;

type SetInfoModal = (modalData: ModalData) => void;

interface PinRegister {
  isActive: boolean;
  invalidAttempts: number;
}

type Navigation = {
  navigate: (screen: string, params?: Record<string, any>) => void;
};

type AddBankMutate = (args: Record<string, any>) => void;

export function onSelectedBankHandler(selectedBank: Bank | null, source: Source): void {
  if (selectedBank) {
    const e = AMPLITUDE_CONSTANTS.bankRegistration.bank_name_chosen;
    const { name, attributes } = e;
    AmplitudeHelper.logEvent(name, {
      [attributes.source]: source,
    });
  }
}

export function onSubmitHandler(
  source: Source,
  defaultBank: { number: string },
  bankList: Bank[],
  t: (key: string) => string,
  setInfoModal: SetInfoModal,
  isFeatureAvailale: Record<string, { show: boolean }>,
  pinRegister: PinRegister,
  navigation: Navigation,
  addBankMutate: AddBankMutate,
) {
  return (values: Values) => {
    logBankAccountAddPress(source, values);

    const defaultBankEntered = values.accountNumber === defaultBank.number;
    const bankExists = bankList?.filter(bank => bank.accountNumber === values.accountNumber) ?? [];

    if (bankExists.length > 0 || defaultBankEntered) {
      const e = AMPLITUDE_CONSTANTS.bankRegistration.add_bank_account_failed;
      const { name, attributes } = e;
      AmplitudeHelper.logEvent(name, {
        [attributes.source]: source,
        [attributes.failure_message]: formatUnderScoreLowerCase(
          t(`login.${BOTTOMSHEET_CONTENT[BOTTONSHEET_KEYS.DUPLICATE_ENTRY].description}`),
        ),
      });

      return setInfoModal({
        visible: true,
        data: returnBottomSheetContent(
          BOTTONSHEET_KEYS.DUPLICATE_ENTRY,
          BOTTOMSHEET_CONTENT[BOTTONSHEET_KEYS.DUPLICATE_ENTRY],
        ),
        errorCode: BOTTONSHEET_KEYS.DUPLICATE_ENTRY,
      });
    }

    if (isFeatureAvailale?.[FEATURE_FLAGS.SECURITY_PIN]?.show && pinRegister.isActive) {
      if (pinRegister.invalidAttempts >= 5) {
        return setInfoModal({
          visible: true,
          data: returnBottomSheetContent(
            SECURITY_PIN_INFO_MODAL.THREE_RETRY_VERIFICATION,
            SECURITY_PIN_INFO_MODAL.THREE_RETRY_VERIFICATION,
          ),
          errorCode: 'THREE_RETRY_VERIFICATION',
        });
      }

      return navigation.navigate(ScreenNames.GG_VERIFY_PIN_WITH_EXPIRY, {
        source: 'add_bank_account',
        pin_failed_modal: 'pin_failed_modal_bankaccount',
        onVerifyCallBack: addBankMutate,
        onVerifyValues: {
          accountNumber: values.accountNumber,
          bankId: values.selectedBank.id,
          beneficiaryName: values.accountName,
          nickName: values.accountNickName,
        },
      });
    }

    addBankMutate({
      accountNumber: values.accountNumber,
      bankId: values.selectedBank.id,
      beneficiaryName: values.accountName,
      nickName: values.accountNickName,
      securityPin: null,
      bioToken: null,
    });
  };
}

export function shouldHandleClose(setInfoModal: SetInfoModal, infoModal: ModalData, addBankMutate: AddBankMutate) {
  return (values: Values) => {
    setInfoModal({ ...infoModal, visible: false });

    if (infoModal.errorCode === 'THREE_RETRY_VERIFICATION') {
      addBankMutate({
        accountNumber: values.accountNumber,
        bankId: values.selectedBank.id,
        beneficiaryName: values.accountName,
        nickName: values.accountNickName,
        securityPin: null,
        bioToken: null,
      });

      logCloseHandler();
    }
  };
}
function GGAddBankScreen(): React.JSX.Element {
  const { bankList, refetch } = useFetchBanks();
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const navigation = useNavigation();
  const [defaultBank, setDefaultBank] = useState<Bank | null>(null);

  const { isSuccess: isSuccessProfile, data: profileData, refetch: refetchProfile } = fetchProfile();

  const { setLoading, setInitialData, pinRegister, setDefaultBank: storeDefaultBank } = getStore();

  const { t } = useTranslation('common');

  const toast = useToast();
  const route = useRoute();
  const { params } = route as { params: { source: string } };
  const { source } = params;

  useEffect(() => {
    refetch();
    refetchProfile();
  }, []);

  useEffect(() => {
    setDefaultBank(profileData?.account || null);
    storeDefaultBank(profileData?.account);
  }, [isSuccessProfile, profileData, storeDefaultBank]);

  useEffect(() => {
    onSelectedBankHandler(selectedBank, source);
  }, [selectedBank, source]);

  const {
    isLoading,
    isSuccess,
    isError,
    error,
    data,
    mutate: addBankMutate,
  } = useMutation({
    mutationFn: (payload: Record<string, any>) => addEmployeeBeneficiaryV2(payload),
  });

  const [isFeatureAvailale] = useFeatureFlag({ featureName: [FEATURE_FLAGS.SECURITY_PIN] });

  const [infoModal, setInfoModal] = useState<InfoModal>({
    visible: false,
    data: returnBottomSheetContent(
      BOTTONSHEET_KEYS.DUPLICATE_ENTRY,
      BOTTOMSHEET_CONTENT[BOTTONSHEET_KEYS.DUPLICATE_ENTRY],
    ),
    errorCode: BOTTONSHEET_KEYS.DUPLICATE_ENTRY,
  });

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    shouldRenderSuccessfulBankAdd(isSuccess, data, toast, t, navigation, params);
  }, [isSuccess, data, toast, t, navigation, params]);

  useEffect(() => {
    shouldShowError(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  const onCloseHandler = shouldHandleClose(setInfoModal, infoModal, addBankMutate);

  const { openBankList, onPressSecondaryHandler } = addBankHandlers({
    setInfoModal,
    infoModal,
    navigation,
    setSelectedBank,
    source,
  });

  const renderFormContent = renderFormContentFunction(
    t,
    openBankList,
    selectedBank,
    setInfoModal,
    infoModal,
    addBankMutate,
    onPressSecondaryHandler,
    onCloseHandler,
    source,
  );

  return (
    <View style={appStyles.safeAreaView}>
      <Formik
        enableReinitialize
        validationSchema={bankAddSchema(t)}
        initialValues={{
          selectedBank,
          accountNumber: '',
          accountName: '',
          accountNickName: '',
        }}
        onSubmit={onSubmitHandler(
          source,
          defaultBank,
          bankList as Bank[],
          t,
          setInfoModal,
          isFeatureAvailale,
          pinRegister,
          navigation,
          addBankMutate,
        )}>
        {renderFormContent}
      </Formik>

      <InfoTextComponent
        containerStyle={styles.infoContainer}
        customTextStyle={styles.textStyle}
        iconColor={colors.neutral.charcoal}
        infoText={t('addBank.bankAccountAddInfo')}
        show
        variant="xsm-bold"
        size={scale(24)}
      />
    </View>
  );
}

GGAddBankScreen.defaultProps = {};
GGAddBankScreen.propTypes = {};

export default GGAddBankScreen;
